import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { PublicKey } from '@solana/web3.js';
import { failoverConnection as rpc } from '../utils/failoverConnection';
import { TOKEN_PROGRAM_ID, unpackAccount } from '@solana/spl-token';
import { CONFIG } from '../config';
import { logger } from '../utils/logger';
import { useWalletContext } from '../context/WalletContext.jsx';
import styles from '../styles/InvestmentPortfolio.module.css';

// Constants for tokens
const CETES_MINT = new PublicKey('CETES7CKqqKQizuSN6iWQwmTeFRjbJR6Vw2XRKfEDR8f');
const GILTS_MINT = new PublicKey('GiLTSeSFnNse7xQVYeKdMyckGw66AoRmyggGg1NNd4yr');
const EUROB_MINT = new PublicKey('EuroszHk1AL7fHBBsxgeGHsamUqwBpb26oEyt9BcfZ6G');
const USTRY_MINT = new PublicKey('USTRYnGgcHAhdWsanv8BG6vHGd4p7UGgoB9NRd8ei7j');
const TESOURO_MINT = new PublicKey('BRNTNaZeTJANz9PeuD8drNbBHwGgg7ZTjiQYrFgWQ48p');
const AMBR_MINT = new PublicKey('XsaQTCgebC2KPbf27KUhdv5JFvHhQ4GDAPURwrEhAzb');
const TSLAX_MINT = new PublicKey('XsDoVfqeBukxuZHWhdvWHBhgEHjGNst4MLodqsJHzoB');
const GOLD_MINT = new PublicKey('Xsv9hRk1z5ystj9MhnA7Lq4vjSsLwzL2nxrwmwtD3re');
const CIRCLE_MINT = new PublicKey('XsueG8BtpquVJX9LVLLEGuViXUungE6WmK5YZ3p3bd1');
const MSTRX_MINT = new PublicKey('4Zmy26CE5u38zSu2zZas8eo5WVn6XDPKrHy1pH9nTREV');
const SP500_MINT = new PublicKey('XsoCS1TfEyfFhfvj8EtZ528L3CaKBDBRqRapnBbDF2W');
const NVIDIA_MINT = new PublicKey('Xsc9qvGR1efVDFGLrVsmkzv3qi45LTBjeUKSPmx9qEh');
const GOOGLE_MINT = new PublicKey('XsCPL9dNWBMvFtTmwcCA5v3xWPSMEBCszbQdiLLq6aN');
const APPLE_MINT = new PublicKey('XsbEhLAtcf6HdfpFZ5xEMdqW8nfAvcsP5bdudRLJzJp');
const NASDAQ_MINT = new PublicKey('Xs8S1uUs1zvS2p7iwtsG3b6fkhpvmwz4GYU3gWAmWHZ');
const ROBINHOOD_MINT = new PublicKey('XsvNBAYkrDRNhA7wPHQfX3ZUXZyZLdnCQDfHZ56bzpg');
const AMAZON_MINT = new PublicKey('Xs3eBt7uRfJX8QUs4suhyU8p2M6DoUDrJyWBa8LLZsg');
const COINBASE_MINT = new PublicKey('Xs7ZdzSHLU9ftNJsii5fCeJhoRWSC32SQGzGQtePxNu');
const META_MINT = new PublicKey('Xsa62P5mvPszXL1krVUnU5ar38bBSVcWAB6fmPCo5Zu');
const MCDONALDS_MINT = new PublicKey('XsqE9cRRpzxcGKDXj1BJ7Xmg4GRhZoyY1KpmGSxAWT2');

// Known mints that we handle specially
const KNOWN_MINTS = new Set([
  CETES_MINT.toBase58(),
  GILTS_MINT.toBase58(),
  EUROB_MINT.toBase58(),
  USTRY_MINT.toBase58(),
  TESOURO_MINT.toBase58(),
  AMBR_MINT.toBase58(),
  TSLAX_MINT.toBase58(),
  GOLD_MINT.toBase58(),
  CIRCLE_MINT.toBase58(),
  MSTRX_MINT.toBase58(),
  SP500_MINT.toBase58(),
  NVIDIA_MINT.toBase58(),
  GOOGLE_MINT.toBase58(),
  APPLE_MINT.toBase58(),
  NASDAQ_MINT.toBase58(),
  ROBINHOOD_MINT.toBase58(),
  AMAZON_MINT.toBase58(),
  COINBASE_MINT.toBase58(),
  META_MINT.toBase58(),
  MCDONALDS_MINT.toBase58(),
]);

const UserInvestmentPortfolio = ({ walletAddress: propWalletAddress }) => {
  const location = useLocation();
  // Prefer prop, then location.state, then context
  const walletAddress = propWalletAddress || location.state?.walletAddress || (useWalletContext()?.walletAddress);
  const { wallet } = useWalletContext();
  // State for token balances
  const [tokenBalances, setTokenBalances] = useState({
    known: {
      CETES: 0,
      GILTS: 0,
      EUROB: 0,
      USTRY: 0,
      TESOURO: 0,
      AMBR: 0,
      TSLAX: 0,
      GOLD: 0,
      CIRCLE: 0,
      MSTRX: 0,
      SP500: 0,
      NVIDIA: 0,
      GOOGLE: 0,
      APPLE: 0,
      NASDAQ: 0,
      ROBINHOOD: 0,
      AMAZON: 0,
      COINBASE: 0,
      META: 0,
      MCDONALDS: 0
    },
    unknown: []
  });
  const [loading, setLoading] = useState(true);
  const [loadingTokens, setLoadingTokens] = useState(true);
  const navigate = useNavigate();

  // Fetch all unknown token balances for the wallet (stocks)
  const fetchAllTokenBalances = async (connection, ownerPubkey) => {
    try {
      setLoadingTokens(true);
      const tokenAccounts = await connection.getTokenAccountsByOwner(ownerPubkey, {
        programId: TOKEN_PROGRAM_ID,
      }).catch(err => {
        logger.error('Error fetching token accounts:', err);
        return { value: [] };
      });

      const balances = [];

      for (const { pubkey, account } of tokenAccounts.value) {
        try {
          if (!account.data) {
            logger.warn('Skipping account with no data:', pubkey.toBase58());
            continue;
          }

          const accountInfo = unpackAccount(account.data);
          const amount = Number(accountInfo.amount);
          const mint = accountInfo.mint.toBase58();
          
          // Skip known mints that are already shown in the main section
          if (KNOWN_MINTS.has(mint) || amount === 0) continue;
          
          // Get token metadata with error handling
          let tokenInfo;
          try {
            tokenInfo = await getTokenInfo(connection, mint);
          } catch (err) {
            logger.warn(`Error fetching token info for mint ${mint}:`, err);
            tokenInfo = {
              symbol: 'UNKNOWN',
              name: 'Unknown Token',
              decimals: 6
            };
          }
          
          balances.push({
            mint,
            amount: amount / Math.pow(10, tokenInfo.decimals || 6),
            symbol: tokenInfo.symbol,
            name: tokenInfo.name,
            decimals: tokenInfo.decimals || 6,
            address: pubkey.toBase58()
          });
        } catch (err) {
          logger.error(`Error processing token account ${pubkey.toBase58()}:`, err);
        }
      }
      
      // Sort by balance (highest first)
      balances.sort((a, b) => b.amount - a.amount);
      
      // Only update state if there are unknown tokens
      if (balances.length > 0) {
        setTokenBalances(prev => ({
          ...prev,
          unknown: balances
        }));
      }
    } catch (error) {
      logger.error('Error fetching token balances:', error);
    } finally {
      setLoadingTokens(false);
    }
  };

  // Helper function to get token info
  const getTokenInfo = async (connection, mintAddress) => {
    // This is a basic implementation - consider using a token registry like Token List
    try {
      const response = await fetch(`https://api.mainnet-beta.solana.com/token/${mintAddress}`);
      if (response.ok) {
        const data = await response.json();
        return {
          symbol: data.symbol || 'Unknown',
          name: data.name || 'Unknown Token',
          decimals: data.decimals || 6
        };
      }
    } catch (error) {
      logger.error('Error fetching token info:', error);
    }
    return { symbol: 'Unknown', name: 'Unknown Token', decimals: 6 };
  };

  // Fetch token balance using parsed token accounts
  const fetchTokenBalance = async (connection, ownerPubkey, mint, mintName) => {
    try {
      const resp = await connection.getParsedTokenAccountsByOwner(ownerPubkey, { 
        programId: TOKEN_PROGRAM_ID,
        mint
      });
      
      console.log(`Raw token accounts for ${mintName} (${mint.toBase58()}):`, resp.value);
      
      let balance = 0;
      for (const { account } of resp.value) {
        const info = account.data.parsed.info;
        console.log(`Token account info for ${mintName}:`, info);
        if (info.mint === mint.toBase58()) {
          console.log(`Found matching mint ${mintName}:`, {
            uiAmount: info.tokenAmount?.uiAmount,
            amount: info.tokenAmount?.amount,
            decimals: info.tokenAmount?.decimals
          });
          // uiAmount is already divided by 10^decimals
          balance += info.tokenAmount?.uiAmount || 0;
        }
      }
      
      return balance;
    } catch (err) {
      logger.error(`Error fetching ${mintName} balance:`, err);
      return 0;
    }
  };

  // Fetch all balances
  useEffect(() => {
    if (!walletAddress) {
      setLoading(false);
      return;
    }
    const fetchBalances = async () => {
      try {
        const connection = rpc;
        const ownerPubkey = new PublicKey(walletAddress);
        
        // Fetch all token balances in parallel
        const [
          cetes, gilts, eurob, ustry, tesouro, ambr,
          tslax, gold, circle, mstrx, sp500, nvidia,
          google, apple, nasdaq, robinhood, amazon,
          coinbase, meta, mcdonalds
        ] = await Promise.all([
          fetchTokenBalance(connection, ownerPubkey, CETES_MINT, 'CETES'),
          fetchTokenBalance(connection, ownerPubkey, GILTS_MINT, 'GILTS'),
          fetchTokenBalance(connection, ownerPubkey, EUROB_MINT, 'EUROB'),
          fetchTokenBalance(connection, ownerPubkey, USTRY_MINT, 'USTRY'),
          fetchTokenBalance(connection, ownerPubkey, TESOURO_MINT, 'TESOURO'),
          fetchTokenBalance(connection, ownerPubkey, AMBR_MINT, 'AMBR'),
          fetchTokenBalance(connection, ownerPubkey, TSLAX_MINT, 'TSLAX'),
          fetchTokenBalance(connection, ownerPubkey, GOLD_MINT, 'GOLD'),
          fetchTokenBalance(connection, ownerPubkey, CIRCLE_MINT, 'CIRCLE'),
          fetchTokenBalance(connection, ownerPubkey, MSTRX_MINT, 'MSTRX'),
          fetchTokenBalance(connection, ownerPubkey, SP500_MINT, 'SP500'),
          fetchTokenBalance(connection, ownerPubkey, NVIDIA_MINT, 'NVIDIA'),
          fetchTokenBalance(connection, ownerPubkey, GOOGLE_MINT, 'GOOGLE'),
          fetchTokenBalance(connection, ownerPubkey, APPLE_MINT, 'APPLE'),
          fetchTokenBalance(connection, ownerPubkey, NASDAQ_MINT, 'NASDAQ'),
          fetchTokenBalance(connection, ownerPubkey, ROBINHOOD_MINT, 'ROBINHOOD'),
          fetchTokenBalance(connection, ownerPubkey, AMAZON_MINT, 'AMAZON'),
          fetchTokenBalance(connection, ownerPubkey, COINBASE_MINT, 'COINBASE'),
          fetchTokenBalance(connection, ownerPubkey, META_MINT, 'META'),
          fetchTokenBalance(connection, ownerPubkey, MCDONALDS_MINT, 'MCDONALDS')
        ]);

        setTokenBalances(prev => ({
          ...prev,
          known: {
            CETES: cetes,
            GILTS: gilts,
            EUROB: eurob,
            USTRY: ustry,
            TESOURO: tesouro,
            AMBR: ambr,
            TSLAX: tslax,
            GOLD: gold,
            CIRCLE: circle,
            MSTRX: mstrx,
            SP500: sp500,
            NVIDIA: nvidia,
            GOOGLE: google,
            APPLE: apple,
            NASDAQ: nasdaq,
            ROBINHOOD: robinhood,
            AMAZON: amazon,
            COINBASE: coinbase,
            META: meta,
            MCDONALDS: mcdonalds
          }
        }));
        
        // Fetch all other token balances (stocks)
        await fetchAllTokenBalances(connection, ownerPubkey);
      } catch (err) {
        logger.error('Error fetching balances:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchBalances();
  }, [walletAddress]);

  // Format number with 2 decimal places
  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  // Investment categories
  const portfolioSections = [
    {
      name: 'Bonds & Stocks',
      items: Object.entries(tokenBalances.known).map(([symbol, amount]) => ({
        name: symbol,
        amount: amount,
        value: amount,
        symbol: symbol
      })).filter(item => item.amount > 0 || item.amount === 0)
    },
    {
      name: 'Other Tokens',
      items: tokenBalances.unknown.map(token => ({
        name: token.symbol,
        amount: token.amount,
        value: token.amount,
        symbol: token.symbol,
        address: token.address
      }))
    }
  ];
  
  // Calculate total value of all investments
  const allInvestments = portfolioSections.flatMap(category => category.items);
  const totalValue = allInvestments.reduce((sum, item) => sum + item.value, 0);

  if (loading || loadingTokens) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Loading portfolio...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Investment Portfolio</h1>
        <button onClick={() => navigate(-1)} className={styles.backButton}>
          &larr; Back
        </button>
      </div>

      <div className={styles.balanceCard}>
        <div className={styles.balanceLabel}>Total Value</div>
        <div className={styles.balanceAmount}>${formatNumber(totalValue)}</div>
      </div>

      <div className={styles.investmentsList}>
        {portfolioSections.map((category, catIndex) => (
          category.items.length > 0 && (
            <div key={catIndex} className={styles.investmentCategory}>
              <h3 className={styles.categoryTitle}>{category.name}</h3>
              <table className={styles.investmentsTable}>
                <thead>
                  <tr>
                    <th>Asset</th>
                    <th>Amount</th>
                    <th>Value</th>
                  </tr>
                </thead>
                <tbody>
                  {category.items.map((item, itemIndex) => (
                    <tr key={`${catIndex}-${itemIndex}`} className={styles.investmentRow}>
                      <td>{item.symbol || item.name}</td>
                      <td>{formatNumber(item.amount)}</td>
                      <td>${formatNumber(item.value)}</td>
                    </tr>
                  ))}
                  {/* Category Total */}
                  <tr className={styles.categoryTotal}>
                    <td colSpan="2">Total {category.name}</td>
                    <td>${formatNumber(category.items.reduce((sum, item) => sum + item.value, 0))}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )
        ))}
      </div>
    </div>
  );
};

export default UserInvestmentPortfolio;